import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaTrash, FaCopy, FaCheck, FaComment, FaChartLine, FaUserTie, FaSitemap, FaBullseye, FaLightbulb, FaTag } from 'react-icons/fa6';
import { useAppContext } from '../../../../shared/context/AppContext';
import { useAnalytics } from '../../../../shared/hooks/useAnalytics';
import { EVENTS } from '../../../../shared/services/analyticsEvents';
import { getUserId } from '../../../../shared/domain/services/userService';
import * as copyController from '../controllers/copyController';
import * as deletePromptController from '../controllers/deletePromptController';
import PromptActionButtons from './PromptActionButtons';
import './PromptCard.css';

// DEMO USER LOGIC COMMENTED OUT - No longer restricting access
// const PromptCard = ({ prompt, isOwnPrompt = false, shouldGreyOutPrompts = false, onDelete, onUpdate, onFavoriteToggle, selectedCategories = [] }) => {
const PromptCard = ({ prompt, isOwnPrompt = false, shouldGreyOutPrompts = false, onDelete, onUpdate, onFavoriteToggle, selectedCategories = [] }) => {
  const navigate = useNavigate();
  const { user, organization } = useAppContext();
  const { trackEvent } = useAnalytics();
  const [copied, setCopied] = useState(false);
  const cardRef = useRef(null);

  // DEMO USER LOGIC COMMENTED OUT - No longer disabling cards
  // Calculate if card should be disabled (greyed out and unclickable)
  // const isDisabled = shouldGreyOutPrompts && !isOwnPrompt;
  // const isDisabled = false; // Always false - no restrictions

  const getCategoryIcon = (category) => {
    if (!category) return FaTag;
    
    const normalizedCategory = category.toLowerCase().trim();
    
    if (normalizedCategory === 'communication') {
      return FaComment;
    } else if (normalizedCategory === 'data & analytics' || normalizedCategory === 'data and analytics') {
      return FaChartLine;
    } else if (normalizedCategory === 'leadership') {
      return FaUserTie;
    } else if (normalizedCategory === 'decision-making' || normalizedCategory === 'decision making') {
      return FaSitemap;
    } else if (normalizedCategory === 'strategy') {
      return FaBullseye;
    } else if (normalizedCategory === 'innovation') {
      return FaLightbulb;
    }
    
    return FaTag;
  };

  const handleCopy = async (e) => {
    e.stopPropagation();
    // DEMO USER LOGIC COMMENTED OUT - No longer disabling copy
    // Disable copy if card is disabled (expired demo user viewing non-own prompt)
    // if (isDisabled) {
    //   return;
    // }
    try {
      const success = await copyController.copyPrompt(prompt.promptText, prompt.id, organization?.id || organization?.orgId);
      if (success) {
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
        // Track copy event
        trackEvent(EVENTS.PROMPT_COPIED, {
          promptId: prompt.id,
          promptTitle: prompt.title,
          isGlobal: prompt.isGlobal || false,
          source: 'prompt_card',
        });
      }
    } catch (error) {
      console.error('Error copying:', error);
    }
  };

  const handleCardClick = () => {
    // DEMO USER LOGIC COMMENTED OUT - No longer disabling navigation
    // Disable navigation if card is disabled (expired demo user viewing non-own prompt)
    // if (isDisabled) {
    //   return;
    // }
    // Track prompt view event
    trackEvent(EVENTS.PROMPT_VIEWED, {
      promptId: prompt.id,
      promptTitle: prompt.title,
      isGlobal: prompt.isGlobal || false,
      categories: prompt.categories || (prompt.category ? [prompt.category] : []),
      source: 'prompt_card',
    });
    navigate(`/prompt/${prompt.id}`);
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    if (!user) return;

    const userId = getUserId(user);
    if (!userId) return;

    try {
      const result = await deletePromptController.deletePrompt(prompt.id, userId);
      if (result.success && onDelete) {
        onDelete(prompt.id);
      }
    } catch (error) {
      console.error('Error deleting prompt:', error);
    }
  };

  // Get categories array, fallback to single category for backward compatibility
  // Handle both cases: categories array exists but might be empty, or single category field
  let categories = [];
  if (prompt.categories && Array.isArray(prompt.categories) && prompt.categories.length > 0) {
    categories = prompt.categories;
  } else if (prompt.category) {
    categories = [prompt.category];
  }
  
  // Limit displayed categories to 2, prioritizing filter matches
  let displayedCategories = [];
  if (selectedCategories && selectedCategories.length > 0) {
    // Filter is active - prioritize matching categories
    const matchedCategories = categories.filter(cat => selectedCategories.includes(cat));
    const unmatchedCategories = categories.filter(cat => !selectedCategories.includes(cat));
    
    // Show matched categories first, then fill remaining slots with unmatched categories
    displayedCategories = [
      ...matchedCategories.slice(0, 2),
      ...unmatchedCategories.slice(0, 2 - matchedCategories.length)
    ].slice(0, 2);
  } else {
    // No filter active - show first 2 categories
    displayedCategories = categories.slice(0, 2);
  }
  
  const CategoryIcon = getCategoryIcon(categories[0]);

  return (
    <>
      <svg style={{ position: 'absolute', width: 0, height: 0 }}>
        <defs>
          <linearGradient id="favorite-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#F93AAC" />
            <stop offset="100%" stopColor="#FF613E" />
          </linearGradient>
          <linearGradient id="category-icon-gradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#1624F0" />
            <stop offset="100%" stopColor="#1019A8" />
          </linearGradient>
        </defs>
      </svg>
      {/* DEMO USER LOGIC COMMENTED OUT - No longer adding disabled class */}
      {/* <div className={`prompt-card-wrapper ${isDisabled ? 'disabled' : ''}`}> */}
      <div className="prompt-card-wrapper" ref={cardRef}>
        <div className="prompt-card" onClick={handleCardClick}>
          <div className="prompt-card-icon-container">
            <div className="prompt-card-icon-placeholder">
              <CategoryIcon className="prompt-card-icon" />
            </div>
          </div>
          <div className="prompt-card-content">
            {isOwnPrompt && (
              <button
                className="delete-button"
                onClick={handleDelete}
                aria-label="Delete prompt"
              >
                <FaTrash className="delete-icon" />
              </button>
            )}
            {displayedCategories.length > 0 && (
              <div className="prompt-category">
                {displayedCategories.map((category, index) => (
                  <span key={index} className="category-label">{category}</span>
                ))}
              </div>
            )}
            
            <h3 className="prompt-title">{prompt.title}</h3>
            
            <div className="prompt-description">
              <p>{prompt.description}</p>
            </div>
            
            <div className="prompt-actions">
              <PromptActionButtons 
                prompt={prompt} 
                stopPropagation={true}
                className=""
                buttonClassName="action-button"
                onUpdate={onUpdate}
                onFavoriteToggle={onFavoriteToggle}
              />
              
              <button
                className={`action-button copy-button ${copied ? 'copied' : ''}`}
                onClick={handleCopy}
                aria-label={copied ? 'Copied!' : 'Copy'}
              >
                {copied ? (
                  <>
                    <FaCheck className="action-icon" />
                    <span className="action-text">Copied!</span>
                  </>
                ) : (
                  <FaCopy className="action-icon" />
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PromptCard;

